/**
 * Live plumbing for the DevOps Overview — a THIN SELECTOR over the shared
 * realtime bus (`src/realtime/`), the same shape as `devopsLiveStatus.ts`
 * (the boards) and `views/home/homeLive.ts` (the dashboard feed).
 *
 * WHY NOT `subscribeDevOpsEvents`: that wrapper hands the boards the flat grid
 * projection and DROPS every envelope that maps to no grid entity — pull
 * requests, releases, anything new. The overview counts exactly those in its
 * rollup, so it must hear every `devops-event` the API publishes, for every
 * connection and project (no scope filter — the rollup is org-wide and
 * re-finding it is one cheap local call). This selector forwards the envelope
 * and nothing else; the store debounces and re-finds.
 *
 * CONNECTION / AUTH / OUTAGE: all owned by the bus — one refcounted socket,
 * the JWT sourced from the AuthStore, a loud diagnostic when the stream is
 * down. See `realtime/realtimeBus.ts` and `realtime/freshnessPolicy.ts`.
 */
import type RootStore from "stores/RootStore";
import { devopsChannel, subscribeRealtime, type DevOpsEnvelope } from "realtime";

type DevOpsOverviewLiveHandler = (envelope: DevOpsEnvelope) => void;

/**
 * Subscribe to EVERY live `devops-event` envelope. Returns an idempotent
 * disposer; the bus drops the socket listeners when the last subscriber
 * across the app leaves.
 */
export function subscribeDevOpsOverviewEvents(
  rootStore: RootStore,
  handler: DevOpsOverviewLiveHandler
): () => void {
  return subscribeRealtime(rootStore, devopsChannel, (event) => {
    handler(event.envelope);
  });
}
